import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse } from '@genial/services';
import { Utils } from '@genial/utils';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../hooks/useToastCreator';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../../../routes/types';
import { useRedemptionActions } from '../../../../store/redemptionSolicitation/actions';
import { fundsWithdrawGetFundAsset } from '../../../../store/redemptionSolicitation/selector';
import { RedemptionError } from '../../components/RedemptionError';
import { RedemptionHeader } from '../../components/RedemptionHeader';
import { RedemptionSuccess } from '../../components/RedemptionSuccess';
import { defaultError } from '../../constants';

import { ConfirmationStep, ConfirmationStepInfos } from './components/ConfirmationStep';
import { ValueSelectionStep } from './components/ValueSelectionStep';
import { ValueSelectionStepLoading } from './components/ValueSelectionStep/loading';
import { validateIsLimitTimeExceeded } from './constants';

export interface FundsRedemptionProps {
  onClose: () => void;
}

export interface ExtraInfoProps extends ConfirmationStepInfos {
  protocol: string;
}

export const FundsRedemption: React.FC<FundsRedemptionProps> = ({ onClose }) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { showToast, showToastFromError } = useToastCreator('funds-redemption');
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'RedemptionSolicitation'>>();

  const [step, setStep] = useState(1);
  const [isExecuting, setIsExecuting] = useState(false);
  const [extraInfo, setExtraInfo] = useState<ExtraInfoProps>();
  const [totalValueOption, setTotalValueOption] = useState(false);
  const [showLimitTimeExceeded, setShowLimitTimeExceeded] = useState(false);
  const [valuesSelected, setValuesSelected] = useState({ grossValue: 0, liquidityValue: 0 });

  const { data, error, refresher, loading } = useRecoilQuery(
    fundsWithdrawGetFundAsset({ accountId: params.accountId, fundId: params.productId }),
    { cachePolicy: 'no-cache' }
  );

  const { executeFundsRedemption } = useRedemptionActions();

  const handleNextStep = (
    totalValueChecked: boolean,
    grossValueSelected: number,
    liquidityValueSelected: number
  ) => {
    setValuesSelected({ grossValue: grossValueSelected, liquidityValue: liquidityValueSelected });
    setTotalValueOption(totalValueChecked);
    setStep(step + 1);
  };

  const handlePressBack = () => {
    if (step === 2) {
      return setStep(1);
    }
    onClose();
  };

  const handleFollowRedemption = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
    navigation.navigate('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          ...params,
          selectedTab: 'transactions',
          movementInitialTab: 'Funds',
        },
      },
    });
  };

  const handleExecuteRedemption = (infos: ConfirmationStepInfos) => {
    if (!data) {
      return;
    }
    setIsExecuting(true);
    executeFundsRedemption({
      accountId: params.accountId,
      amount: valuesSelected.grossValue,
      hasRedemptionAnticipated: false,
      movimentDate: infos.selectedDate,
      operationType: totalValueOption ? 'TotalRedemption' : 'GrossRedemption',
      portfolioId: data.portfolioId,
      productId: data.fund.fundId,
      interactionChannel: infos.channel,
      observation: infos.observation,
    })
      .then(({ data: executeDate }) => {
        setExtraInfo({ ...infos, protocol: executeDate.externalId || '--' });
        setStep(step + 1);
      })
      .catch((executeError) => {
        const isLimitTimeExceeded = validateIsLimitTimeExceeded(
          infos.selectedDate,
          data.fund.cutoffTime
        );
        setShowLimitTimeExceeded(isLimitTimeExceeded);
        if (instanceOfErrorListResponse(executeError)) {
          (executeError as unknown as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }
        showToast(...defaultError);
      })
      .then(() => setIsExecuting(false));
  };

  const handleRefresh = () => {
    refresher();
    setStep(1);
  };

  const renderStep = () => {
    if (step === 99) {
      return <RedemptionError onRefresh={handleRefresh} />;
    }
    if (loading || !data) {
      return <ValueSelectionStepLoading productName={params.productName} />;
    }

    switch (step) {
      case 1:
        return <ValueSelectionStep handleNextStep={handleNextStep} fundAssetNormalized={data} />;

      case 2:
        return (
          <ConfirmationStep
            handleCancel={onClose}
            handleConfirm={handleExecuteRedemption}
            isExecuting={isExecuting}
            accountId={params.accountId}
            totalValueOption={totalValueOption}
            fundAssetNormalized={data}
            valuesSelected={valuesSelected}
            showLimitTimeExceeded={showLimitTimeExceeded}
            setShowLimitTimeExceeded={setShowLimitTimeExceeded}
          />
        );

      case 3:
        return (
          <RedemptionSuccess
            infos={[
              {
                label: 'Valor resgatado',
                value: Utils.Mask.Money(valuesSelected.liquidityValue || valuesSelected.grossValue),
              },
              { label: 'Protocolo', value: extraInfo?.protocol },
              {
                label: 'Data de resgate',
                value: Utils.Mask.DateFormat(extraInfo?.selectedDate, 'dd/MM/yyyy'),
              },
              { label: 'Data de cotização', value: extraInfo?.quotationDateFormatted },
              { label: 'Data de liquidação', value: extraInfo?.liquidityDateFormatted },
            ]}
            onClose={onClose}
            onFollowRedemption={handleFollowRedemption}
          />
        );

      default:
        return null;
    }
  };

  useEffect(() => {
    if (error) {
      setStep(99);
    }
  }, [error]);

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <RedemptionHeader onClose={handlePressBack} numberOfSteps={2} currentStep={step} />
      {renderStep()}
    </VStack>
  );
};
